/**
 * The values grid — one row per reporting unit, two boxes per row (§D3 manual submissions).
 *
 * Deliberately **not** `RowsGrid`. That grid is a spreadsheet over an unknown table: a
 * click-to-edit cell, a roving focus over an arbitrary number of columns, a window over
 * fifty thousand rows. This one is a form with a fixed shape — a unit, what is on file for
 * it, what was on file the period before, a number and a note — and a reporter filling
 * eighty of them wants to Tab from box to box, not to press Enter to open each one. So
 * every editable cell is a real `<input>` from the start, and the keyboard behaviour is
 * the browser's, which is the behaviour a form has taught everyone to expect.
 *
 * The state is still `grid.ts`'s: `initGrid` reads the `/current` answer, `applyEdit`
 * writes a cell, `dirtyCount` says how many rows the submit would change, and dirtiness is
 * measured against what the server sent — so typing a value back to what was already on
 * file leaves the row clean and the counter honest.
 *
 * **`previous` is read-only and in its own column.** It is the single most useful thing a
 * reporter can see while typing a number — "last period this was 11" catches a slipped
 * decimal that no range check would — and putting it in the box would invite it to be
 * submitted as this period's answer.
 */
import { useTranslation } from 'react-i18next';

import type { IndicatorCurrentUnit } from '../../../lib/api';
import { cellOf, isDirtyRow, type GridState } from '../grid';
import { rowIdOf } from './values';

export interface ValuesGridProps {
  units: readonly IndicatorCurrentUnit[];
  state: GridState;
  /** Raw text out of a box; the panel coerces it and decides what to do with a refusal. */
  onCell: (rowId: string, key: 'value' | 'note', raw: string) => void;
  /** Row id → the key whose box holds something that is not a number. */
  errors: Record<string, 'value'>;
  /**
   * What is literally in a value box, where that differs from the coerced cell.
   *
   * A controlled input fed only from `grid.ts` would fight the person typing: `coerceCell`
   * reads `1,` as 1 (a French decimal comma mid-type), so the box would snap back to `1`
   * and the comma could never be followed by a digit. The panel keeps the keystrokes; the
   * grid state keeps the meaning.
   */
  drafts: Record<string, string>;
  /** Formats a number the way the indicator's own `fmt` asks for. */
  fmt: (value: number | null) => string;
}

export function ValuesGrid({ units, state, onCell, errors, drafts, fmt }: ValuesGridProps) {
  const { t } = useTranslation();

  return (
    <div className="mgr-grid-scroll mgr-iv-scroll">
      <table className="mgr-grid-table mgr-iv-table" data-testid="mgr-iv-grid">
        <thead>
          <tr>
            <th scope="col">{t('manager.values.col.unit')}</th>
            <th scope="col">{t('manager.values.col.country')}</th>
            <th scope="col" className="ro num">
              {t('manager.values.col.previous')}
            </th>
            <th scope="col" className="num">
              {t('manager.values.col.value')}
            </th>
            <th scope="col">{t('manager.values.col.note')}</th>
          </tr>
        </thead>
        <tbody>
          {units.map((unit) => {
            const id = rowIdOf(unit);
            const row = state.rows.find((r) => r.id === id);
            const value = row ? cellOf(row, 'value') : null;
            const note = row ? cellOf(row, 'note') : null;
            const dirty = row ? isDirtyRow(state, row) : false;
            const bad = errors[id] === 'value';
            return (
              <tr
                key={id}
                className={`mgr-grid-row${dirty ? ' dirty' : ''}`}
                data-testid={`mgr-iv-row-${id}`}
              >
                <th scope="row" className="mgr-iv-unit">
                  {unit.name}
                  {unit.id ? <code>{unit.id}</code> : null}
                </th>
                <td>{unit.country ?? ''}</td>
                <td className="ro num" data-testid={`mgr-iv-prev-${id}`}>
                  {fmt(unit.previous)}
                </td>
                <td className={`num${bad ? ' bad' : ''}`}>
                  <input
                    className="mgr-grid-input"
                    data-testid={`mgr-iv-cell-${id}-value`}
                    type="text"
                    inputMode="decimal"
                    aria-label={t('manager.values.cellLabel', { unit: unit.name })}
                    aria-invalid={bad || undefined}
                    value={drafts[id] ?? (value === null ? '' : String(value))}
                    onChange={(e) => onCell(id, 'value', e.target.value)}
                  />
                </td>
                <td>
                  <input
                    className="mgr-grid-input"
                    data-testid={`mgr-iv-cell-${id}-note`}
                    type="text"
                    aria-label={t('manager.values.noteLabel', { unit: unit.name })}
                    value={note === null ? '' : String(note)}
                    onChange={(e) => onCell(id, 'note', e.target.value)}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default ValuesGrid;
